import { useMemo } from 'react';
import {
  avgDaysPerStage, createdVsCompleted, funnel, inPeriod, lastWeeks, stagePerWeek, tiles, topStalled, type Period,
} from '../../lib/tripAnalytics';
import { STAGES, STAGE_LABEL, SUB_LABEL, type Journey, type Stage } from '../../lib/tripPipeline';

// Hand-drawn SVG, no chart library. One colour per stage, light to dark along the journey.
const STAGE_COLOR: Record<string, string> = {
  chat: '#c4b5fd', payment: '#a78bfa', handbook: '#8b5cf6',
  trip_started: '#7c3aed', trip_completed: '#6d28d9', survey_answered: '#4c1d95',
};

const weekLabel = (iso: string) => new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

function Tile({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-app-border bg-app-card px-4 py-3">
      <div className="text-[11px] text-app-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-app-fg">{value}</div>
      {hint && <div className="text-[11px] text-app-muted">{hint}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-app-border bg-app-card p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-app-muted">{title}</h3>
      {children}
    </section>
  );
}

function Funnel({ data }: { data: { stage: Stage; count: number }[] }) {
  const max = Math.max(1, data[0]?.count ?? 1);
  return (
    <ul className="flex flex-col gap-2">
      {data.map((d, i) => {
        const prev = i > 0 ? data[i - 1].count : null;
        return (
          <li key={d.stage} className="flex items-center gap-3 text-xs">
            <span className="w-32 shrink-0 text-app-fg">{STAGE_LABEL[d.stage]}</span>
            <div className="h-5 flex-1 rounded bg-app-bg">
              <div className="h-5 rounded" style={{ width: `${(d.count / max) * 100}%`, background: STAGE_COLOR[d.stage] }} />
            </div>
            <span className="w-8 text-right font-medium text-app-fg">{d.count}</span>
            <span className="w-12 text-right text-[11px] text-app-muted">{prev ? `${Math.round((d.count / prev) * 100)}%` : ''}</span>
          </li>
        );
      })}
    </ul>
  );
}

const W = 520, H = 170, PAD_L = 24, PAD_B = 22, PAD_T = 8;

function Axis({ weeks, max }: { weeks: string[]; max: number }) {
  const bw = (W - PAD_L) / weeks.length;
  return (
    <>
      {[0, 0.5, 1].map((f) => {
        const y = PAD_T + (H - PAD_T - PAD_B) * (1 - f);
        return (
          <g key={f}>
            <line x1={PAD_L} x2={W} y1={y} y2={y} stroke="currentColor" strokeOpacity={0.12} />
            <text x={PAD_L - 4} y={y + 3} textAnchor="end" fontSize={9} fill="currentColor" opacity={0.6}>{Math.round(max * f)}</text>
          </g>
        );
      })}
      {weeks.map((w, i) => (i % 2 === 0 || weeks.length <= 6) && (
        <text key={w} x={PAD_L + bw * i + bw / 2} y={H - 6} textAnchor="middle" fontSize={9} fill="currentColor" opacity={0.6}>{weekLabel(w)}</text>
      ))}
    </>
  );
}

function StackedWeeks({ data }: { data: { week: string; byStage: Record<string, number> }[] }) {
  const totals = data.map((d) => Object.values(d.byStage).reduce((a, b) => a + b, 0));
  const max = Math.max(1, ...totals);
  const bw = (W - PAD_L) / data.length;
  const ch = H - PAD_T - PAD_B;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Journeys created per week, by current stage" className="w-full text-app-fg">
      <Axis weeks={data.map((d) => d.week)} max={max} />
      {data.map((d, i) => {
        let y = H - PAD_B;
        return STAGES.map((s) => {
          const n = d.byStage[s] ?? 0;
          if (!n) return null;
          const h = (n / max) * ch;
          y -= h;
          return (
            <rect key={`${d.week}${s}`} x={PAD_L + bw * i + bw * 0.15} y={y} width={bw * 0.7} height={h} fill={STAGE_COLOR[s]}>
              <title>{`${weekLabel(d.week)} · ${STAGE_LABEL[s]}: ${n}`}</title>
            </rect>
          );
        });
      })}
    </svg>
  );
}

function CreatedCompleted({ data }: { data: { week: string; created: number; completed: number }[] }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.created, d.completed]));
  const bw = (W - PAD_L) / data.length;
  const ch = H - PAD_T - PAD_B;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Journeys created and trips completed per week" className="w-full text-app-fg">
      <Axis weeks={data.map((d) => d.week)} max={max} />
      {data.map((d, i) => (
        <g key={d.week}>
          <rect x={PAD_L + bw * i + bw * 0.12} y={H - PAD_B - (d.created / max) * ch} width={bw * 0.36} height={(d.created / max) * ch} fill="#a78bfa">
            <title>{`${weekLabel(d.week)} · created: ${d.created}`}</title>
          </rect>
          <rect x={PAD_L + bw * i + bw * 0.52} y={H - PAD_B - (d.completed / max) * ch} width={bw * 0.36} height={(d.completed / max) * ch} fill="#4c1d95">
            <title>{`${weekLabel(d.week)} · completed: ${d.completed}`}</title>
          </rect>
        </g>
      ))}
    </svg>
  );
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-app-muted">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm" style={{ background: i.color }} />{i.label}
        </span>
      ))}
    </div>
  );
}

export function TripDashboard({ journeys, period, onPeriod, onOpen }: {
  journeys: Journey[];
  period: Period;
  onPeriod: (p: Period) => void;
  onOpen: (j: Journey) => void;
}) {
  const rows = useMemo(() => inPeriod(journeys, period), [journeys, period]);
  const t = useMemo(() => tiles(rows), [rows]);
  const weeks = useMemo(() => lastWeeks(period === '30' ? 5 : 12), [period]);
  const stalled = useMemo(() => topStalled(rows), [rows]);
  const avg = useMemo(() => avgDaysPerStage(rows), [rows]);

  return (
    <div className="flex flex-col gap-4">
      <div className="inline-flex self-start rounded-lg border border-app-border bg-app-card p-0.5 text-xs">
        {([['30', 'Last 30 days'], ['90', 'Last 90 days'], ['all', 'All time']] as const).map(([v, label]) => (
          <button
            key={v} type="button" aria-pressed={period === v} onClick={() => onPeriod(v)}
            className={`rounded-md px-3 py-1.5 font-medium ${period === v ? 'bg-violet text-white' : 'text-app-muted hover:text-app-fg'}`}
          >{label}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Tile label="Journeys" value={t.total} hint="created in period" />
        <Tile label="Active" value={t.active} hint="chat to trip started" />
        <Tile label="Needs attention" value={t.needsAttention} />
        <Tile label="Trips completed" value={t.completed} />
        <Tile label="Survey answered" value={t.surveyRate === null ? '—' : `${Math.round(t.surveyRate * 100)}%`} hint="of completed trips" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Funnel"><Funnel data={funnel(rows)} /></Card>
        <Card title="Average days between steps">
          <ul className="flex flex-col gap-2 text-xs">
            {avg.map((a) => (
              <li key={a.label} className="flex items-center justify-between gap-3 border-b border-app-border pb-2 last:border-0">
                <span className="text-app-fg">{a.label}</span>
                <span className="text-app-muted">
                  {a.days === null ? 'no data' : <><span className="font-medium text-app-fg">{a.days.toFixed(1)}d</span> · {a.n} trip{a.n === 1 ? '' : 's'}</>}
                </span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Created per week, by current stage">
          <StackedWeeks data={stagePerWeek(rows, weeks)} />
          <Legend items={STAGES.map((s) => ({ label: STAGE_LABEL[s], color: STAGE_COLOR[s] }))} />
        </Card>
        <Card title="Created vs completed per week">
          <CreatedCompleted data={createdVsCompleted(rows, weeks)} />
          <Legend items={[{ label: 'Created', color: '#a78bfa' }, { label: 'Completed', color: '#4c1d95' }]} />
        </Card>
      </div>

      <Card title="Waiting longest">
        {stalled.length === 0 ? <p className="text-xs text-app-muted">Nothing is waiting.</p> : (
          <ul className="divide-y divide-app-border">
            {stalled.map((j) => (
              <li key={j.journey_id}>
                <button type="button" onClick={() => onOpen(j)} className="flex w-full items-center justify-between gap-3 py-2 text-left text-xs hover:bg-app-bg/50">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-app-fg">{j.trip_name || 'Untitled trip'}</span>
                    <span className="block truncate text-[11px] text-app-muted">{j.traveler_name ?? 'Traveler'}{j.host_name ? ` → ${j.host_name}` : ''}</span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block text-app-fg">{STAGE_LABEL[j.stage]} · {SUB_LABEL[j.sub_state] ?? j.sub_state}</span>
                    <span className="block text-[11px] text-app-muted">{j.days_in_stage}d{j.flags.length ? ` · ${j.flags.length} flag${j.flags.length === 1 ? '' : 's'}` : ''}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
